import styles from 'css/src/components/app/template-preview/TemplateSearchButton.module.scss';

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { getTemplateSearchQuery, setTemplateSearchQuery } from 'js/src/data/templates';

type TemplateSearchButtonProps = {
    onExpandedChange?: (isExpanded: boolean) => void;
};

export const TemplateSearchButton: React.FC<TemplateSearchButtonProps> = ({ onExpandedChange }) => {
    const dispatch = useDispatch();
    const searchQuery = useSelector(getTemplateSearchQuery);
    const [isExpanded, setIsExpanded] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);

    const hasContent = searchQuery.trim().length > 0;

    // Notify parent when expanded state changes
    React.useEffect(() => {
        onExpandedChange?.(isExpanded);
    }, [isExpanded, onExpandedChange]);

    const handleSearchClick = () => {
        setIsExpanded(true);
        // Focus the input after expansion animation starts
        setTimeout(() => {
            inputRef.current?.focus();
        }, 50);
    };

    const handleBlur = () => {
        if (searchQuery.trim().length > 0) {
            analytics.track('Template Search Blurred', {
                searchTerm: searchQuery.trim(),
            });
        }

        if (!hasContent) {
            setIsExpanded(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setTemplateSearchQuery({ query: e.target.value }));
    };

    const handleClear = () => {
        dispatch(setTemplateSearchQuery({ query: '' }));
        setIsExpanded(false);
    };

    // Keep expanded if there's content
    React.useEffect(() => {
        if (hasContent) {
            setIsExpanded(true);
        }
    }, [hasContent]);

    return (
        <div
            className={classNames(
                styles['template-search-button'],
                'd-flex align-items-center flex-shrink-0',
                {
                    [styles['expanded']]: isExpanded,
                },
            )}
        >
            {!isExpanded ? (
                <button
                    type="button"
                    className={classNames(
                        styles['search-trigger'],
                        'template-category-nav-button py-l5-8 px-l5-12 d-flex align-items-center gap-1',
                    )}
                    onClick={handleSearchClick}
                    aria-label="Search templates"
                >
                    <i className="material-symbols-outlined">search</i>
                    Search
                </button>
            ) : (
                <div
                    className={`${styles['search-input-container']} d-flex align-items-center bg-white rounded-2 px-l5-16 gap-2 w-100`}
                >
                    <i
                        className={`material-symbols-outlined ${styles['search-icon']} flex-shrink-0`}
                    >
                        search
                    </i>
                    <input
                        ref={inputRef}
                        type="text"
                        className={`${styles['search-input']} flex-grow-1 border-0 bg-transparent py-l5-8 text-grey-900`}
                        placeholder="Search templates..."
                        value={searchQuery}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        aria-label="Search templates"
                    />
                    {hasContent && (
                        <button
                            className={`${styles['clear-button']} d-flex align-items-center justify-content-center border-0 bg-transparent text-grey-600 cursor-pointer p-l5-4 rounded-1 flex-shrink-0`}
                            onClick={handleClear}
                            aria-label="Clear search"
                        >
                            <i className="material-symbols-outlined">close</i>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};
